export function isStudentValidityExpired(user) {
  const v = user?.studentValidUntil;
  if (!v) return false;
  return new Date(v) <= new Date();
}

export function isStudentExpired(user) {
  if (!user || user.accountType !== "student") return false;
  if (user.studentStatus === "expired") return true;
  if (user.studentStatus === "verified" && isStudentValidityExpired(user)) return true;
  return false;
}

export function isVerifiedStudentUser(user) {
  if (!user || user.studentStatus !== "verified") return false;
  if (isStudentValidityExpired(user)) return false;
  return true;
}

/** One-time student menu preview before restaurant verification (user agreed to show ID at pickup). */
export function isUnverifiedStudentFirstOrderPreview(user) {
  if (!user || user.accountType !== "student") return false;
  if (user.studentShowIdAtRestaurantConsent !== true) return false;
  if (user.studentUnverifiedOfferUsed === true) return false;
  if (String(user.studentStatus || "") !== "none") return false;
  return true;
}

/** Whole years between a date of birth and now, or null if no/invalid DOB. */
export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

/** Owner id(s) that refused this account for the student discount, as strings. */
export function declinedOwnerIds(user) {
  const list = user?.studentDeclinedOwnerIds || user?.studentDeclinedByOwners || [];
  return list
    .map((entry) => (entry && entry.ownerId != null ? entry.ownerId : entry))
    .filter((id) => id != null)
    .map(String);
}

/**
 * Per-restaurant gate on top of base student eligibility:
 * - age limit: when policy.studentMaxAge > 0, require a known DOB within the limit
 * - decline: the restaurant owner must not have refused this account
 */
export function passesOwnerStudentGate(user, ctx) {
  if (!ctx) return true;
  const { policy, restaurantOwnerId } = ctx;

  const maxAge = Number(policy?.studentMaxAge || 0);
  if (maxAge > 0) {
    const age = ageFromDob(user?.dateOfBirth);
    if (age == null || age > maxAge) return false;
  }

  if (restaurantOwnerId && declinedOwnerIds(user).includes(String(restaurantOwnerId))) {
    return false;
  }

  return true;
}

export function isStudentRestaurantPricingEligible(user, ctx) {
  const base = isVerifiedStudentUser(user) || isUnverifiedStudentFirstOrderPreview(user);
  if (!base) return false;
  return passesOwnerStudentGate(user, ctx);
}

/** Window (in days) before expiry where we nudge the student/owner to renew. */
export const STUDENT_RENEWAL_WINDOW_DAYS = 30;

/** Whole days from now until `validUntil` (rounded up), or null when unknown/invalid. */
export function daysUntil(validUntil) {
  if (!validUntil) return null;
  const until = new Date(validUntil);
  if (Number.isNaN(until.getTime())) return null;
  const ms = until.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/** Verified student whose validity ends within `withinDays` (and not already expired). */
export function isStudentExpiringSoon(user, withinDays = STUDENT_RENEWAL_WINDOW_DAYS) {
  if (!isVerifiedStudentUser(user)) return false;
  const days = daysUntil(user?.studentValidUntil);
  return days != null && days >= 0 && days <= withinDays;
}

/**
 * High-level surface state for a student account:
 * - "none": not a student account → nothing to surface
 * - "notActivated": student account that isn't verified yet (or expired)
 * - "expiringSoon": verified but within the renewal window
 * - "ok": verified with comfortable validity
 */
export function studentSurfaceState(user, withinDays = STUDENT_RENEWAL_WINDOW_DAYS) {
  if (!user || user.accountType !== "student") return "none";
  if (isStudentExpired(user) || user.needsStudentReverification) return "expired";
  if (!isVerifiedStudentUser(user)) return "notActivated";
  if (isStudentExpiringSoon(user, withinDays)) return "expiringSoon";
  return "ok";
}
